'use client';

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useOrderStore } from '@/store/useOrderStore';
import { cn } from '@/lib/utils';
import { Upload, X, Link as LinkIcon, HelpCircle, Image as ImageIcon, ChevronDown } from 'lucide-react';

export default function Step1CustomUpload() {
  const { itemInfo, setItemInfo, phone, setPhone } = useOrderStore();
  const [linkUrl, setLinkUrl] = useState(itemInfo.customData?.linkUrl || '');
  const [description, setDescription] = useState(itemInfo.customData?.description || '');
  const [imageUrls, setImageUrls] = useState<string[]>(itemInfo.customData?.imageUrls || []);
  const [uploading, setUploading] = useState(false);
  const [showCarrotGuide, setShowCarrotGuide] = useState(false);
  const [inputMode, setInputMode] = useState<'link' | 'photo' | null>(
    (itemInfo.customData?.imageUrls?.length ?? 0) > 0 && !itemInfo.customData?.linkUrl
      ? 'photo'
      : itemInfo.customData?.linkUrl
        ? 'link'
        : null,
  );

  const updateCustomData = (data: Partial<{ imageUrls: string[]; linkUrl: string; description: string }>) => {
    const newCustomData = {
      imageUrls: data.imageUrls ?? imageUrls,
      linkUrl: data.linkUrl ?? linkUrl,
      description: data.description ?? description,
    };
    setItemInfo({
      isCustom: true,
      itemId: null,
      category: '커스텀 물품',
      basePrice: 0,
      options: {},
      customData: newCustomData,
    });
  };

  const handleLinkChange = (value: string) => {
    setLinkUrl(value);
    updateCustomData({ linkUrl: value });
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    updateCustomData({ description: value });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (imageUrls.length + files.length > 5) {
      alert('이미지는 최대 5장까지 업로드할 수 있습니다.');
      return;
    }

    setUploading(true);

    const newUrls: string[] = [];
    for (const file of Array.from(files)) {
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name}의 크기가 10MB를 초과합니다.`);
        continue;
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch('/api/media/quote-upload', { method: 'POST', body: formData });
        const data = await res.json();
        if (data.success && data.data?.url) {
          newUrls.push(data.data.url);
        } else {
          alert(`${file.name} 업로드 실패`);
        }
      } catch {
        alert(`${file.name} 업로드 중 오류 발생`);
      }
    }

    const updatedUrls = [...imageUrls, ...newUrls];
    setImageUrls(updatedUrls);
    updateCustomData({ imageUrls: updatedUrls });
    setUploading(false);
  };

  const removeImage = (index: number) => {
    const updatedUrls = imageUrls.filter((_, i) => i !== index);
    setImageUrls(updatedUrls);
    updateCustomData({ imageUrls: updatedUrls });
  };

  return (
    <div className="space-y-4">
      {/* 설명 */}
      <div className="space-y-2">
        <Label>물품 설명</Label>
        <Textarea
          value={description}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          placeholder="물품의 종류와 크기, 재질, 특이사항 등을 적어주세요."
          maxLength={500}
          rows={4}
        />
        <div className="text-xs text-gray-400 text-right">{description.length}/500</div>
      </div>

      {/* 입력 방식 토글 */}
      <div className="space-y-2">
        <Label>물품 링크 또는 사진</Label>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setInputMode('link')}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-all',
              inputMode === 'link'
                ? 'bg-[#1A3A5C] text-white border-[#1A3A5C]'
                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400',
            )}
          >
            <LinkIcon className="w-3.5 h-3.5" />
            물품 링크
          </button>
          <button
            type="button"
            onClick={() => setInputMode('photo')}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-all',
              inputMode === 'photo'
                ? 'bg-[#1A3A5C] text-white border-[#1A3A5C]'
                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400',
            )}
          >
            <ImageIcon className="w-3.5 h-3.5" />
            물품 사진
          </button>
        </div>
      </div>

      {/* 물품 링크 입력 */}
      {inputMode === 'link' && (
        <div className="space-y-2">
          <Input
            type="url"
            value={linkUrl}
            onChange={(e) => handleLinkChange(e.target.value)}
            placeholder="물품 정보가 있는 링크를 입력해주세요."
          />
          <button
            type="button"
            onClick={() => setShowCarrotGuide(!showCarrotGuide)}
            className="flex items-center justify-between w-full px-3 py-2.5 bg-orange-50 border border-orange-200 hover:bg-orange-100 rounded-lg transition-colors"
          >
            <span className="flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-700">당근 물품 링크 가져오기</span>
            </span>
            <ChevronDown
              className={cn(
                'w-4 h-4 text-orange-600 transition-transform',
                showCarrotGuide && 'rotate-180',
              )}
            />
          </button>
          {showCarrotGuide && (
            <div className="rounded-lg overflow-hidden border border-orange-200">
              <img
                src="/images/carrot_guide.jpg"
                alt="당근 물품 링크 복사 가이드"
                className="w-full"
              />
            </div>
          )}
        </div>
      )}

      {/* 물품 사진 업로드 */}
      {inputMode === 'photo' && (
        <div className="space-y-2">
          {/* 업로드된 이미지 미리보기 */}
          {imageUrls.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {imageUrls.map((url, index) => (
                <div key={index} className="relative w-20 h-20">
                  <img
                    src={url}
                    alt={`업로드 이미지 ${index + 1}`}
                    className="w-full h-full object-cover rounded-lg border"
                  />
                  <button
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* 업로드 영역 */}
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors bg-gray-50">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <Upload className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">
                {uploading ? '업로드 중...' : '클릭하거나 파일을 드래그하세요. (최대 5장)'}
              </p>
              <p className="text-xs text-gray-400 mt-1">JPEG, PNG, HEIC (최대 10MB)</p>
            </div>
            <input
              type="file"
              accept="image/jpeg,image/png,image/heic"
              multiple
              onChange={handleFileUpload}
              disabled={uploading || imageUrls.length >= 5}
              className="hidden"
            />
          </label>
        </div>
      )}

      {/* 전화번호 */}
      <div className="space-y-2">
        <Label>연락처 (견적 안내 문자 수신용)</Label>
        <Input
          type="tel"
          inputMode="numeric"
          value={phone}
          onChange={(e) => {
            const numbers = e.target.value.replace(/\D/g, '');
            let formatted = numbers;
            if (numbers.length <= 3) formatted = numbers;
            else if (numbers.length <= 7) formatted = `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
            else formatted = `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
            setPhone(formatted);
          }}
          placeholder="010-0000-0000"
          maxLength={13}
        />
        {phone && !/^01[016789]-\d{3,4}-\d{4}$/.test(phone) && (
          <p className="text-sm text-red-500">올바른 휴대폰 번호를 입력해주세요</p>
        )}
      </div>

      {/* 안내문 */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
        <p className="text-sm text-yellow-800">
          문의하신 물품은 운송매니저 확인 후 기본 운송비와 함께 신청링크를 문자로 전달 드립니다.
        </p>
      </div>
    </div>
  );
}
